import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { submitLead } from "@/lib/forms/submit-lead.functions";
import type { SubmitLeadInput } from "@/lib/forms/schemas";
import {
  LEAD_RETRY_DELAYS_MS,
  clearLeadDraft,
  createTokenWaiter,
  finalLeadError,
  restoreLeadDraft,
  saveLeadDraft,
  sleep,
} from "./lead-retry";

export interface LeadSubmitOptions {
  /**
   * Resets Turnstile and resolves with a fresh token (a token is single-use).
   * Without it, or when it resolves null, a failed send is not retried.
   */
  nextToken?: () => Promise<string | null>;
  /** The form, so its fields are kept in this tab while a send fails. */
  form?: HTMLFormElement | null;
}

export type SubmitStatus = "idle" | "submitting" | "success" | "error";

function readUtm(): SubmitLeadInput["utm"] | undefined {
  if (typeof window === "undefined") return undefined;
  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: params.get("utm_source") ?? undefined,
    utm_medium: params.get("utm_medium") ?? undefined,
    utm_campaign: params.get("utm_campaign") ?? undefined,
    utm_term: params.get("utm_term") ?? undefined,
    utm_content: params.get("utm_content") ?? undefined,
  };
  const hasAny = Object.values(utm).some(Boolean);
  return hasAny ? utm : undefined;
}

export function useLeadSubmit() {
  const submit = useServerFn(submitLead);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (
      input: SubmitLeadInput extends infer T
        ? T extends SubmitLeadInput
          ? Omit<T, "page_path" | "referrer" | "utm">
          : never
        : never,
      opts: LeadSubmitOptions = {},
    ) => {
      setStatus("submitting");
      setError(null);
      const draftKey = `form:${input.form_key}`;
      let enriched = {
        ...input,
        page_path:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        referrer: typeof document !== "undefined" ? document.referrer : undefined,
        utm: readUtm(),
      } as SubmitLeadInput;
      // A failed send is retried twice (2 s, then 5 s), each time with a
      // fresh Turnstile token got the normal way (14 Oct 2026).
      for (let attempt = 0; ; attempt += 1) {
        try {
          const res = await submit({ data: enriched });
          clearLeadDraft(draftKey);
          setStatus("success");
          return res;
        } catch (err) {
          saveLeadDraft(draftKey, opts.form ?? null);
          const delay = LEAD_RETRY_DELAYS_MS[attempt];
          if (delay !== undefined && opts.nextToken) {
            setError("Still sending, one moment…");
            await sleep(delay);
            const token = await opts.nextToken();
            if (token) {
              enriched = { ...enriched, turnstile_token: token } as SubmitLeadInput;
              continue;
            }
          }
          setError(finalLeadError(err));
          setStatus("error");
          return null;
        }
      }
    },
    [submit],
  );

  return { send, status, error, reset: () => setStatus("idle") };
}

/**
 * Turnstile token plumbing for a website form (14 Oct 2026): the current
 * token, the widget props, and nextToken() for a retry, which resets the
 * widget and waits for its next token. Also restores a kept draft into the
 * form after a reload.
 */
export function useFormTurnstile(formKey: string) {
  const [token, setToken] = useState<string | null>(null);
  const resetRef = useRef<(() => void) | null>(null);
  const waiterRef = useRef(createTokenWaiter());
  const formRef = useRef<HTMLFormElement | null>(null);
  // The widget is hidden (interaction-only): a failed check is shown under
  // the form instead (turnstile-ui.ts). A later token clears it.
  const [verifyFailed, setVerifyFailed] = useState(false);
  useEffect(() => {
    restoreLeadDraft(`form:${formKey}`, formRef.current);
  }, [formKey]);
  const onToken = useCallback((t: string) => {
    setToken(t);
    setVerifyFailed(false);
    waiterRef.current.onToken(t);
  }, []);
  const onError = useCallback(() => {
    setToken(null);
    setVerifyFailed(true);
  }, []);
  const nextToken = useCallback(async () => {
    setToken(null);
    const t = await waiterRef.current.next(resetRef.current);
    // The retry spends this token; the button waits for the next one.
    setToken(null);
    return t;
  }, []);
  return { token, setToken, onToken, onError, verifyFailed, resetRef, formRef, nextToken };
}
